import { useState, useEffect } from 'react';
import {
  PanelTopOpen, Plus, Trash2, Copy, Check, ChevronDown, ChevronUp,
  AlertCircle, X, GripVertical, Code2, Eye,
} from 'lucide-react';
import Layout from '../components/Layout';
import TagCreator from '../components/TagCreator';
import { PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { popupsApi, platformConnectionsApi } from '../lib/api';
import type { Popup, PopupField, PopupSubmission, PopupEmbed, PopupFieldType } from '../types/popups';
import type { PlatformConnection } from '../types/vault';

const CF_FIELDS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Custom attribute' },
  { value: 'email_address', label: 'Email' },
  { value: 'first_name', label: 'First name' },
  { value: 'last_name', label: 'Last name' },
  { value: 'phone_number', label: 'Phone' },
];

const FIELD_TYPES: PopupFieldType[] = ['text', 'email', 'phone', 'textarea', 'select', 'hidden'];

export default function Popups() {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [newName, setNewName] = useState('');
  const [newConnId, setNewConnId] = useState('');
  const [creating, setCreating] = useState(false);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [edit, setEdit] = useState<Popup | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [cfTags, setCfTags] = useState<Array<{ id: number; name: string; color: string }>>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [subs, setSubs] = useState<PopupSubmission[]>([]);
  const [embed, setEmbed] = useState<PopupEmbed | null>(null);
  const [tab, setTab] = useState<'fields' | 'design' | 'embed' | 'submissions'>('fields');
  const [showPreview, setShowPreview] = useState(false);

  const loadPopups = async () => {
    try {
      const res = await popupsApi.list();
      if (res.success && res.data) setPopups((res.data as unknown as { popups: Popup[] }).popups || []);
    } catch {
      setError('Failed to load popups');
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    try {
      const res = await platformConnectionsApi.list();
      if (res.success && res.data) {
        const all = (res.data as unknown as { connections: PlatformConnection[] }).connections || [];
        setConnections(all.filter((c) => c.platform === 'clickfunnels' && c.status === 'active'));
      }
    } catch {}
  };

  useEffect(() => {
    loadPopups();
    loadConnections();
  }, []);

  const handleCreate = async () => {
    if (!newName.trim() || !newConnId) return;
    setCreating(true);
    try {
      const res = await popupsApi.create(newName.trim(), newConnId);
      if (res.success) {
        setShowCreateModal(false);
        setNewName('');
        setNewConnId('');
        loadPopups();
      } else {
        setError(res.message || 'Failed to create');
      }
    } catch {
      setError('Failed to create popup');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this popup?')) return;
    try {
      await popupsApi.delete(id);
      setPopups((prev) => prev.filter((p) => p.id !== id));
      if (expandedId === id) { setExpandedId(null); setEdit(null); }
    } catch {
      setError('Failed to delete');
    }
  };

  const handleExpand = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); setEdit(null); return; }
    setExpandedId(id);
    setTab('fields');
    setEmbed(null);
    try {
      const res = await popupsApi.get(id);
      if (res.success && res.data) setEdit((res.data as unknown as { popup: Popup }).popup);
    } catch {
      setError('Failed to load popup');
    }
    loadTags(id);
    loadSubs(id);
  };

  const handleSave = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const res = await popupsApi.update(edit.id, {
        name: edit.name,
        fields: edit.fields,
        styleConfig: edit.styleConfig,
        settings: edit.settings,
      });
      if (res.success && res.data) {
        const updated = (res.data as unknown as { popup: Popup }).popup;
        setEdit(updated);
        setPopups((prev) => prev.map((p) => (p.id === updated.id ? { ...p, name: updated.name } : p)));
      } else {
        setError(res.message || 'Failed to save');
      }
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (popup: Popup) => {
    const status = popup.status === 'published' ? 'draft' : 'published';
    try {
      const res = await popupsApi.update(popup.id, { status });
      if (res.success) {
        loadPopups();
        if (edit?.id === popup.id) {
          setEdit((prev) => (prev ? { ...prev, status: status as Popup['status'] } : prev));
          if (status === 'published') loadEmbed(popup.id);
        }
      }
    } catch {
      setError('Failed to update status');
    }
  };

  const loadTags = async (id: string) => {
    setLoadingTags(true);
    try {
      const res = await popupsApi.getTags(id);
      if (res.success && res.data) setCfTags((res.data as unknown as { tags: Array<{ id: number; name: string; color: string }> }).tags || []);
    } catch {}
    setLoadingTags(false);
  };

  const loadSubs = async (id: string) => {
    try {
      const res = await popupsApi.getSubmissions(id);
      if (res.success && res.data) setSubs((res.data as unknown as { submissions: PopupSubmission[] }).submissions || []);
    } catch {}
  };

  const loadEmbed = async (id: string) => {
    try {
      const res = await popupsApi.getEmbed(id);
      if (res.success && res.data) setEmbed(res.data as unknown as PopupEmbed);
    } catch {}
  };

  // ── Field editing ──────────────────────────────────────────
  const addField = () => {
    if (!edit) return;
    const f: PopupField = {
      id: crypto.randomUUID().slice(0, 8),
      type: 'text',
      label: 'New field',
      placeholder: '',
      required: false,
      cfField: null,
      customAttributeKey: null,
      options: [],
    };
    setEdit({ ...edit, fields: [...edit.fields, f] });
  };

  const updateField = (id: string, patch: Partial<PopupField>) => {
    if (!edit) return;
    setEdit({ ...edit, fields: edit.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)) });
  };

  const removeField = (id: string) => {
    if (!edit) return;
    setEdit({ ...edit, fields: edit.fields.filter((f) => f.id !== id) });
  };

  const moveField = (idx: number, dir: -1 | 1) => {
    if (!edit) return;
    const arr = [...edit.fields];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setEdit({ ...edit, fields: arr });
  };

  const setSetting = <K extends keyof Popup['settings']>(key: K, value: Popup['settings'][K]) => {
    if (!edit) return;
    setEdit({ ...edit, settings: { ...edit.settings, [key]: value } });
  };

  const setStyle = <K extends keyof Popup['styleConfig']>(key: K, value: Popup['styleConfig'][K]) => {
    if (!edit) return;
    setEdit({ ...edit, styleConfig: { ...edit.styleConfig, [key]: value } });
  };

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  function hexToRgba(hex: string, a: number): string {
    if (typeof hex !== 'string' || hex.charAt(0) !== '#') return hex;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const n = parseInt(h, 16);
    if (isNaN(n)) return hex;
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  }

  function timeAgo(ts: number): string {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  const tabBtn = (id: typeof tab, label: string) => (
    <button
      onClick={() => { setTab(id); if (id === 'embed' && edit && edit.status === 'published') loadEmbed(edit.id); }}
      className={`px-3 py-1.5 text-sm font-medium rounded-[10px] ${tab === id ? 'bg-accent text-white' : 'text-ink-2 hover:bg-surface-2'}`}
    >
      {label}
    </button>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <PageHeader
          eyebrow="Lead capture"
          title="Popups"
          description="A one-script lead-capture modal. It syncs to ClickFunnels and remembers the visitor in their browser — so it shows once, and your page can read the lead."
          actions={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>New Popup</Button>}
        />

        {error && (
          <div className="mb-4 p-3 bg-danger-soft border border-line rounded-lg flex items-center gap-2 text-danger text-sm">
            <AlertCircle className="w-4 h-4" /> <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-auto text-danger hover:opacity-70"><X className="w-4 h-4" /></button>
          </div>
        )}

        <div className="mb-6 bg-accent-soft border border-line rounded-lg p-4 text-sm text-ink-2">
          <p className="font-medium mb-1">How it works</p>
          <p>
            Paste one <code className="bg-surface-2 text-ink font-mono px-1 rounded">&lt;script&gt;</code> tag into any site or vibe-coded app.
            First-time visitors get the popup; on submit, the lead is saved to ClickFunnels and cached in their browser.
            Returning visitors skip it, and your page can read the lead via <code className="bg-surface-2 text-ink font-mono px-1 rounded">window.MyLeadSyncer.getLead()</code>.
            The <strong>Embed</strong> tab includes a ready-to-paste guide for any LLM.
          </p>
        </div>

        {/* List */}
        {popups.length === 0 ? (
          <EmptyState
            icon={PanelTopOpen}
            title="No popups yet"
            description="Create your first one."
            action={<Button icon={Plus} onClick={() => setShowCreateModal(true)}>New Popup</Button>}
          />
        ) : (
          <div className="space-y-3">
            {popups.map((popup) => (
              <div key={popup.id} className="ds-card overflow-hidden hover:-translate-y-0.5 hover:shadow-card transition-all">
                <div className="flex items-center px-3 md:px-5 py-3 md:py-4 cursor-pointer hover:bg-surface-2" onClick={() => handleExpand(popup.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                      <span className="font-medium text-ink truncate">{popup.name}</span>
                      <Badge tone={popup.status === 'published' ? 'success' : 'neutral'}>{popup.status}</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-ink-3">
                      <span className="truncate font-mono">/p/{popup.slug}</span>
                      {popup.connectionName && <span className="hidden sm:inline">via {popup.connectionName}</span>}
                      <span>{popup.submissionCount || 0} leads</span>
                      <span>{timeAgo(popup.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleTogglePublish(popup); }}
                      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                      style={{ background: popup.status === 'published' ? 'var(--success)' : 'var(--ink-3)' }}
                      title={popup.status === 'published' ? 'Unpublish' : 'Publish'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${popup.status === 'published' ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(popup.id); }} className="p-1.5 text-ink-3 hover:text-danger" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedId === popup.id ? <ChevronUp className="w-4 h-4 text-ink-3" /> : <ChevronDown className="w-4 h-4 text-ink-3" />}
                  </div>
                </div>

                {/* Editor */}
                {expandedId === popup.id && edit && (
                  <div className="border-t border-line p-4 md:p-6">
                    <div className="flex items-center gap-2 mb-5 flex-wrap">
                      {tabBtn('fields', 'Fields')}
                      {tabBtn('design', 'Design & Behavior')}
                      {tabBtn('embed', 'Embed')}
                      {tabBtn('submissions', `Leads (${subs.length})`)}
                      <div className="ml-auto flex items-center gap-2">
                        <Button variant="ghost" icon={Eye} onClick={() => setShowPreview(true)}>Preview</Button>
                        <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
                      </div>
                    </div>

                    {/* FIELDS TAB */}
                    {tab === 'fields' && (
                      <div>
                        <div className="mb-4">
                          <label className="ds-label">Popup Name</label>
                          <input type="text" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="ds-input w-full max-w-md" />
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-display text-sm font-semibold text-ink">Fields</h3>
                          <Button variant="primary" icon={Plus} className="!h-8 !px-3 text-xs" onClick={addField}>Add Field</Button>
                        </div>

                        <div className="space-y-3">
                          {edit.fields.map((f, idx) => (
                            <div key={f.id} className="border border-line rounded-lg p-3 bg-surface-2">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="flex flex-col">
                                  <button onClick={() => moveField(idx, -1)} className="text-ink-3 hover:text-ink-2"><ChevronUp className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => moveField(idx, 1)} className="text-ink-3 hover:text-ink-2"><ChevronDown className="w-3.5 h-3.5" /></button>
                                </div>
                                <GripVertical className="w-4 h-4 text-ink-3" />
                                <select value={f.type} onChange={(e) => updateField(f.id, { type: e.target.value as PopupFieldType })} className="ds-input">
                                  {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <input type="text" value={f.label} onChange={(e) => updateField(f.id, { label: e.target.value })} placeholder="Label" className="ds-input flex-1" />
                                <label className="flex items-center gap-1 text-xs text-ink-2 whitespace-nowrap">
                                  <input type="checkbox" checked={f.required} onChange={(e) => updateField(f.id, { required: e.target.checked })} /> req
                                </label>
                                <button onClick={() => removeField(f.id)} className="text-ink-3 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-ink-2 mb-1">{f.type === 'hidden' ? 'Hidden value' : 'Placeholder'}</label>
                                  <input type="text" value={f.placeholder} onChange={(e) => updateField(f.id, { placeholder: e.target.value })} className="ds-input w-full" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-ink-2 mb-1">Maps to</label>
                                  <select
                                    value={f.cfField || ''}
                                    onChange={(e) => updateField(f.id, { cfField: e.target.value || null, customAttributeKey: e.target.value ? null : f.customAttributeKey })}
                                    className="ds-input w-full"
                                  >
                                    {CF_FIELDS.map((cf) => <option key={cf.value} value={cf.value}>{cf.label}</option>)}
                                  </select>
                                </div>
                                {!f.cfField && (
                                  <div>
                                    <label className="block text-xs font-medium text-ink-2 mb-1">Custom attribute key</label>
                                    <input type="text" value={f.customAttributeKey || ''} onChange={(e) => updateField(f.id, { customAttributeKey: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() })} placeholder="e.g. company_name" className="ds-input w-full font-mono" />
                                  </div>
                                )}
                                {f.type === 'select' && (
                                  <div className="sm:col-span-2">
                                    <label className="block text-xs font-medium text-ink-2 mb-1">Options (one per line)</label>
                                    <textarea value={f.options.join('\n')} onChange={(e) => updateField(f.id, { options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })} rows={3} className="ds-input w-full" />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* DESIGN & BEHAVIOR TAB */}
                    {tab === 'design' && (
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-display text-sm font-semibold text-ink mb-3">Copy</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><label className="block text-xs font-medium text-ink-2 mb-1">Headline</label><input type="text" value={edit.settings.headline} onChange={(e) => setSetting('headline', e.target.value)} className="ds-input w-full" /></div>
                            <div><label className="block text-xs font-medium text-ink-2 mb-1">Button text</label><input type="text" value={edit.styleConfig.buttonText} onChange={(e) => setStyle('buttonText', e.target.value)} className="ds-input w-full" /></div>
                            <div className="sm:col-span-2"><label className="block text-xs font-medium text-ink-2 mb-1">Description</label><input type="text" value={edit.settings.description} onChange={(e) => setSetting('description', e.target.value)} className="ds-input w-full" /></div>
                            <div className="sm:col-span-2"><label className="block text-xs font-medium text-ink-2 mb-1">Success message</label><input type="text" value={edit.settings.successMessage} onChange={(e) => setSetting('successMessage', e.target.value)} className="ds-input w-full" /></div>
                          </div>
                        </div>

                        <div>
                          <h3 className="font-display text-sm font-semibold text-ink mb-3">Behavior</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-ink-2 mb-1">Trigger</label>
                              <select value={edit.settings.trigger} onChange={(e) => setSetting('trigger', e.target.value as Popup['settings']['trigger'])} className="ds-input w-full">
                                <option value="immediate">On page load</option>
                                <option value="delay">After delay</option>
                                <option value="exit">On exit intent</option>
                              </select>
                            </div>
                            {edit.settings.trigger === 'delay' && (
                              <div><label className="block text-xs font-medium text-ink-2 mb-1">Delay (seconds)</label><input type="number" min={0} value={edit.settings.delaySeconds} onChange={(e) => setSetting('delaySeconds', Number(e.target.value))} className="ds-input w-full" /></div>
                            )}
                            <div><label className="block text-xs font-medium text-ink-2 mb-1">Remember for (days)</label><input type="number" min={0} value={edit.settings.rememberDays} onChange={(e) => setSetting('rememberDays', Number(e.target.value))} className="ds-input w-full" /></div>
                          </div>

                          {/* Force / hard gate */}
                          <div className={`mt-3 rounded-lg border p-3 ${edit.settings.dismissible === false ? 'border-warning bg-warning-soft' : 'border-line bg-surface-2'}`}>
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium text-ink">Force the popup (require submission)</p>
                                <p className="text-xs text-ink-3 mt-0.5">Removes the ✕ close button, blocks scrolling, and won't let visitors into the app until they fill it out.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setSetting('dismissible', edit.settings.dismissible === false)}
                                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0"
                                style={{ background: edit.settings.dismissible === false ? 'var(--warning)' : 'var(--ink-3)' }}
                                aria-pressed={edit.settings.dismissible === false}
                                title={edit.settings.dismissible === false ? 'Forced — click to allow closing' : 'Click to force'}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${edit.settings.dismissible === false ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="font-display text-sm font-semibold text-ink mb-3">Style</h3>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {([['primaryColor', 'Button'], ['backgroundColor', 'Background'], ['textColor', 'Text'], ['overlayColor', 'Overlay']] as const).map(([k, lbl]) => (
                              <div key={k}>
                                <label className="block text-xs font-medium text-ink-2 mb-1">{lbl}</label>
                                <input type="color" value={edit.styleConfig[k]} onChange={(e) => setStyle(k, e.target.value)} className="w-full h-9 border border-line rounded cursor-pointer" />
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h3 className="font-display text-sm font-semibold text-ink mb-2">Tags applied on submit</h3>
                          {loadingTags ? (
                            <p className="text-xs text-ink-3">Loading tags...</p>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {cfTags.map((tag) => {
                                const selected = edit.settings.tagIds.includes(String(tag.id));
                                return (
                                  <button
                                    key={tag.id}
                                    onClick={() => setSetting('tagIds', selected ? edit.settings.tagIds.filter((t) => t !== String(tag.id)) : [...edit.settings.tagIds, String(tag.id)])}
                                    className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${selected ? 'bg-accent-soft border-accent text-accent-ink' : 'bg-surface border-line text-ink-3 hover:border-ink-3'}`}
                                  >
                                    {selected && <Check className="w-3 h-3 inline mr-1" />}{tag.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {!loadingTags && edit.connectionId && (
                            <TagCreator
                              connectionId={edit.connectionId}
                              onCreated={(tag) => {
                                setCfTags((prev) => (prev.some((t) => t.id === tag.id) ? prev : [...prev, tag]));
                                if (!edit.settings.tagIds.includes(String(tag.id))) setSetting('tagIds', [...edit.settings.tagIds, String(tag.id)]);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* EMBED TAB */}
                    {tab === 'embed' && (
                      <div>
                        {edit.status !== 'published' ? (
                          <div className="p-3 bg-warning-soft border border-line rounded-lg text-warning text-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" /> Publish this popup to get the embed code and integration guide.
                          </div>
                        ) : (
                          <div className="space-y-6">
                            <p className="text-sm text-ink-2">Two ways to add this popup. <strong>Pick one</strong> — you don't need both.</p>

                            {/* Option A — AI integration (recommended) */}
                            <div className="border border-line bg-accent-soft rounded-lg p-4">
                              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                                <h3 className="font-display text-sm font-semibold text-ink flex items-center gap-1.5"><Code2 className="w-4 h-4 text-accent-ink" /> Option A — Building with an AI? Copy this guide</h3>
                                {embed && (
                                  <Button variant="primary" className="!h-8 !px-3 text-xs" onClick={() => copy(embed.integrationGuide, 'guide')}>
                                    {copied === 'guide' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copy for AI (includes script)
                                  </Button>
                                )}
                              </div>
                              <p className="text-xs text-ink-3 mb-1">Paste into Claude / Cursor / v0 / etc. and it will wire the popup in correctly.</p>
                              <p className="text-xs text-accent-ink mb-3">✓ The embed script is already inside this guide — you don't need Option B as well.</p>
                              {embed ? (
                                <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap max-h-96">{embed.integrationGuide}</pre>
                              ) : (
                                <button onClick={() => loadEmbed(edit.id)} className="text-sm text-accent-ink hover:underline">Load integration guide</button>
                              )}
                            </div>

                            {/* Option B — manual */}
                            <div className="border border-line rounded-lg p-4">
                              <h3 className="font-display text-sm font-semibold text-ink mb-1">Option B — Adding it yourself? Paste this script</h3>
                              <p className="text-xs text-ink-3 mb-3">Drop this one tag into your page — in the <code className="bg-surface-2 text-ink font-mono px-1 rounded">&lt;head&gt;</code> or just before <code className="bg-surface-2 text-ink font-mono px-1 rounded">&lt;/body&gt;</code>, both work. Keep <code className="bg-surface-2 text-ink font-mono px-1 rounded">defer</code>. (Only needed if you're <em>not</em> using Option A.)</p>
                              <div className="relative">
                                <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3 font-mono text-xs overflow-x-auto">{popupsApi.getScriptCode(edit.slug)}</pre>
                                <button onClick={() => copy(popupsApi.getScriptCode(edit.slug), 'script')} className="absolute top-2 right-2 p-1.5 bg-surface-2 rounded text-ink-3 hover:text-ink">
                                  {copied === 'script' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                              <a href={popupsApi.getPopupUrl(edit.slug)} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs text-accent-ink hover:underline">Open live preview →</a>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* SUBMISSIONS TAB */}
                    {tab === 'submissions' && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-display text-sm font-semibold text-ink">Recent Leads</h3>
                          <button onClick={() => loadSubs(edit.id)} className="text-xs text-accent-ink hover:underline">Refresh</button>
                        </div>
                        {subs.length === 0 ? (
                          <div className="text-center py-6 border border-dashed border-line rounded-lg"><p className="text-ink-3 text-sm">No leads captured yet.</p></div>
                        ) : (
                          <div className="border border-line rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-surface-2">
                                <tr>
                                  <th className="text-left px-3 py-2 text-xs font-medium text-ink-3">Email</th>
                                  <th className="text-left px-3 py-2 text-xs font-medium text-ink-3 hidden sm:table-cell">Name</th>
                                  <th className="text-left px-3 py-2 text-xs font-medium text-ink-3">Status</th>
                                  <th className="text-right px-3 py-2 text-xs font-medium text-ink-3">When</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subs.map((s) => (
                                  <tr key={s.id} className="border-t border-line-2 hover:bg-surface-2">
                                    <td className="px-3 py-2 text-ink truncate max-w-[200px]">{String((s.data as any)?.email || '—')}</td>
                                    <td className="px-3 py-2 text-ink-2 hidden sm:table-cell">{String((s.data as any)?.name || '—')}</td>
                                    <td className="px-3 py-2"><Badge tone={s.status === 'success' ? 'success' : 'danger'}>{s.status}</Badge></td>
                                    <td className="px-3 py-2 text-ink-3 text-xs text-right whitespace-nowrap">{timeAgo(s.createdAt)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Live Preview (rendered from current, unsaved edit state) */}
        {showPreview && edit && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: hexToRgba(edit.styleConfig.overlayColor || '#000000', 0.55), fontFamily: `${edit.styleConfig.fontFamily || 'Inter'}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` }}
            onClick={() => setShowPreview(false)}
          >
            <div
              className="relative w-full max-w-[400px] max-h-[90vh] overflow-auto text-center"
              style={{ background: edit.styleConfig.backgroundColor || '#FFFFFF', borderRadius: edit.styleConfig.borderRadius || '12px', padding: '26px 24px', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {edit.settings.dismissible !== false && (
                <button onClick={() => setShowPreview(false)} aria-label="Close" className="absolute" style={{ top: 14, right: 18, border: 'none', background: 'transparent', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: '#9ca3af' }}>×</button>
              )}

              {edit.settings.headline && <h3 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: edit.styleConfig.textColor || '#111827' }}>{edit.settings.headline}</h3>}
              {edit.settings.description && <p style={{ margin: '0 0 18px', fontSize: 14, color: edit.styleConfig.textColor || '#111827', opacity: 0.7 }}>{edit.settings.description}</p>}

              {edit.fields.filter((f) => f.type !== 'hidden').map((f) => {
                const inputStyle: React.CSSProperties = {
                  width: '100%', padding: '10px 12px', border: `1px solid ${edit.styleConfig.inputBorderColor || '#D1D5DB'}`,
                  borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none',
                  background: edit.styleConfig.inputBackgroundColor || '#FFFFFF', color: edit.styleConfig.textColor || '#111827',
                };
                return (
                  <div key={f.id} style={{ marginBottom: 12, textAlign: 'left' }}>
                    {f.label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: edit.styleConfig.textColor || '#111827' }}>{f.label}{f.required ? ' *' : ''}</label>}
                    {f.type === 'textarea' ? (
                      <textarea rows={3} placeholder={f.placeholder} style={{ ...inputStyle, resize: 'vertical' }} readOnly />
                    ) : f.type === 'select' ? (
                      <select style={inputStyle} defaultValue="">
                        <option value="">{f.placeholder || 'Select...'}</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : 'text'} placeholder={f.placeholder} style={inputStyle} readOnly />
                    )}
                  </div>
                );
              })}

              <button type="button" style={{ width: '100%', marginTop: 16, padding: 12, border: 'none', borderRadius: 8, background: edit.styleConfig.primaryColor || '#3B82F6', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {edit.styleConfig.buttonText || 'Continue'}
              </button>
              <div style={{ marginTop: 14, fontSize: 11, color: '#9ca3af' }}>Secured by MyLeadSyncer</div>
              {edit.settings.dismissible === false && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#d97706' }}>Forced — no close button for visitors. (Click outside to exit this preview.)</div>
              )}
            </div>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="ds-card shadow-card p-4 md:p-6 w-full max-w-md">
              <h2 className="font-display text-lg font-semibold text-ink mb-4">New Popup</h2>
              <div className="space-y-4">
                <div>
                  <label className="ds-label">Name</label>
                  <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. App Signup Gate" className="ds-input w-full" autoFocus />
                </div>
                <div>
                  <label className="ds-label">ClickFunnels Connection</label>
                  {connections.length === 0 ? (
                    <p className="text-sm text-warning">No active ClickFunnels connections found. Add one in Vault &gt; Connections first.</p>
                  ) : (
                    <select value={newConnId} onChange={(e) => setNewConnId(e.target.value)} className="ds-input w-full">
                      <option value="">Select connection...</option>
                      {connections.map((c) => <option key={c.id} value={c.id}>{c.displayName || c.workspaceName || c.platform}</option>)}
                    </select>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => { setShowCreateModal(false); setNewName(''); setNewConnId(''); }}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !newName.trim() || !newConnId}>{creating ? 'Creating...' : 'Create'}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
